import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { isAuthenticated, isEmailVerified, getAuthConfig, logout, getUser } from '../utils/auth';
import AppNav from '../components/AppNav';
import './SubscriptionsPage.css';

// ── Branded modal/dialog component ───────────────────────────────────────────
// Props:
//   title, message, type ('info'|'warning'|'error'|'success')
//   onClose  — called on backdrop click or Cancel/OK
//   onConfirm — if provided, shows Cancel + Confirm buttons
//   confirmLabel — label for the confirm button (default 'Confirm')
function BrandedModal({ title, message, type = 'info', onClose, onConfirm, confirmLabel = 'Confirm' }) {
  if (!title && !message) return null;
  const iconMap = {
    info:    { icon: 'fa-solid fa-circle-info',           color: '#40E0D0' },
    warning: { icon: 'fa-solid fa-triangle-exclamation',  color: '#f59e0b' },
    error:   { icon: 'fa-solid fa-circle-xmark',          color: '#f87171' },
    success: { icon: 'fa-solid fa-circle-check',          color: '#10b981' },
  };
  const { icon, color } = iconMap[type] || iconMap.info;
  const btnBase = {
    border: 'none', borderRadius: '8px', padding: '9px 20px',
    fontSize: '14px', fontWeight: 700, cursor: 'pointer',
  };
  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        zIndex: 1000, padding: '20px',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#1e1e1e', border: `1px solid ${color}44`,
          borderRadius: '14px', padding: '28px 28px 24px',
          maxWidth: '440px', width: '100%',
          boxShadow: `0 0 40px ${color}18, 0 8px 32px rgba(0,0,0,0.5)`,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '14px', marginBottom: '22px' }}>
          <i className={icon} style={{ color, fontSize: '22px', marginTop: '2px', flexShrink: 0 }} />
          <div>
            {title && (
              <p style={{ color: '#fff', fontWeight: 700, fontSize: '16px', margin: '0 0 6px' }}>{title}</p>
            )}
            {message && (
              <p style={{ color: '#aaa', fontSize: '14px', lineHeight: 1.55, margin: 0 }}>{message}</p>
            )}
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
          {onConfirm ? (
            <>
              <button onClick={onClose} style={{ ...btnBase, background: 'transparent', color: '#aaa', border: '1px solid #444' }}>
                Cancel
              </button>
              <button
                onClick={() => { onClose(); onConfirm(); }}
                style={{ ...btnBase, background: 'linear-gradient(135deg, #40E0D0 0%, #20B2AA 100%)', color: '#1a1a1a' }}
              >
                {confirmLabel}
              </button>
            </>
          ) : (
            <button onClick={onClose} style={{ ...btnBase, background: 'linear-gradient(135deg, #40E0D0 0%, #20B2AA 100%)', color: '#1a1a1a' }}>
              OK
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

const API_URL = process.env.REACT_APP_API_URL || window.location.origin;

const SERVICE_NAMES = {
  n8n: 'n8n', wordpress: 'WordPress', ghost: 'Ghost', openclaw: 'OpenClaw',
  nextcloud: 'Nextcloud', gitea: 'Gitea', mattermost: 'Mattermost',
  plausible: 'Plausible', appsmith: 'Appsmith', supabase: 'Supabase'
};
const getServiceName = (key) =>
  SERVICE_NAMES[key] || (key ? key.charAt(0).toUpperCase() + key.slice(1) : 'Other');

const getSupportTier = (planSlug) => {
  if (!planSlug) return null;
  const slug = planSlug.toLowerCase();
  if (slug.includes('business')) return { label: 'Fast-Response Support', desc: 'Same-day response during business hours', cls: 'tier-business', icon: 'fa-bolt' };
  if (slug.includes('pro'))      return { label: 'Priority Support', desc: 'Response within 24 hours', cls: 'tier-pro', icon: 'fa-star' };
  return { label: 'Community Support', desc: 'Responses may take 2–5 business days', cls: 'tier-starter', icon: 'fa-users' };
};

const COUNTRIES = [
  { code: 'AF', name: 'Afghanistan' }, { code: 'AL', name: 'Albania' }, { code: 'DZ', name: 'Algeria' },
  { code: 'AD', name: 'Andorra' }, { code: 'AO', name: 'Angola' }, { code: 'AG', name: 'Antigua and Barbuda' },
  { code: 'AR', name: 'Argentina' }, { code: 'AM', name: 'Armenia' }, { code: 'AU', name: 'Australia' },
  { code: 'AT', name: 'Austria' }, { code: 'AZ', name: 'Azerbaijan' }, { code: 'BS', name: 'Bahamas' },
  { code: 'BH', name: 'Bahrain' }, { code: 'BD', name: 'Bangladesh' }, { code: 'BB', name: 'Barbados' },
  { code: 'BY', name: 'Belarus' }, { code: 'BE', name: 'Belgium' }, { code: 'BZ', name: 'Belize' },
  { code: 'BJ', name: 'Benin' }, { code: 'BT', name: 'Bhutan' }, { code: 'BO', name: 'Bolivia' },
  { code: 'BA', name: 'Bosnia and Herzegovina' }, { code: 'BW', name: 'Botswana' }, { code: 'BR', name: 'Brazil' },
  { code: 'BN', name: 'Brunei' }, { code: 'BG', name: 'Bulgaria' }, { code: 'BF', name: 'Burkina Faso' },
  { code: 'BI', name: 'Burundi' }, { code: 'CV', name: 'Cabo Verde' }, { code: 'KH', name: 'Cambodia' },
  { code: 'CM', name: 'Cameroon' }, { code: 'CA', name: 'Canada' }, { code: 'CF', name: 'Central African Republic' },
  { code: 'TD', name: 'Chad' }, { code: 'CL', name: 'Chile' }, { code: 'CN', name: 'China' },
  { code: 'CO', name: 'Colombia' }, { code: 'KM', name: 'Comoros' }, { code: 'CG', name: 'Congo' },
  { code: 'CR', name: 'Costa Rica' }, { code: 'HR', name: 'Croatia' }, { code: 'CU', name: 'Cuba' },
  { code: 'CY', name: 'Cyprus' }, { code: 'CZ', name: 'Czech Republic' }, { code: 'DK', name: 'Denmark' },
  { code: 'DJ', name: 'Djibouti' }, { code: 'DM', name: 'Dominica' }, { code: 'DO', name: 'Dominican Republic' },
  { code: 'EC', name: 'Ecuador' }, { code: 'EG', name: 'Egypt' }, { code: 'SV', name: 'El Salvador' },
  { code: 'GQ', name: 'Equatorial Guinea' }, { code: 'ER', name: 'Eritrea' }, { code: 'EE', name: 'Estonia' },
  { code: 'SZ', name: 'Eswatini' }, { code: 'ET', name: 'Ethiopia' }, { code: 'FJ', name: 'Fiji' },
  { code: 'FI', name: 'Finland' }, { code: 'FR', name: 'France' }, { code: 'GA', name: 'Gabon' },
  { code: 'GM', name: 'Gambia' }, { code: 'GE', name: 'Georgia' }, { code: 'DE', name: 'Germany' },
  { code: 'GH', name: 'Ghana' }, { code: 'GR', name: 'Greece' }, { code: 'GD', name: 'Grenada' },
  { code: 'GT', name: 'Guatemala' }, { code: 'GN', name: 'Guinea' }, { code: 'GW', name: 'Guinea-Bissau' },
  { code: 'GY', name: 'Guyana' }, { code: 'HT', name: 'Haiti' }, { code: 'HN', name: 'Honduras' },
  { code: 'HK', name: 'Hong Kong' }, { code: 'HU', name: 'Hungary' }, { code: 'IS', name: 'Iceland' },
  { code: 'IN', name: 'India' }, { code: 'ID', name: 'Indonesia' }, { code: 'IR', name: 'Iran' },
  { code: 'IQ', name: 'Iraq' }, { code: 'IE', name: 'Ireland' }, { code: 'IL', name: 'Israel' },
  { code: 'IT', name: 'Italy' }, { code: 'JM', name: 'Jamaica' }, { code: 'JP', name: 'Japan' },
  { code: 'JO', name: 'Jordan' }, { code: 'KZ', name: 'Kazakhstan' }, { code: 'KE', name: 'Kenya' },
  { code: 'KI', name: 'Kiribati' }, { code: 'KW', name: 'Kuwait' }, { code: 'KG', name: 'Kyrgyzstan' },
  { code: 'LA', name: 'Laos' }, { code: 'LV', name: 'Latvia' }, { code: 'LB', name: 'Lebanon' },
  { code: 'LS', name: 'Lesotho' }, { code: 'LR', name: 'Liberia' }, { code: 'LY', name: 'Libya' },
  { code: 'LI', name: 'Liechtenstein' }, { code: 'LT', name: 'Lithuania' }, { code: 'LU', name: 'Luxembourg' },
  { code: 'MO', name: 'Macao' }, { code: 'MG', name: 'Madagascar' }, { code: 'MW', name: 'Malawi' },
  { code: 'MY', name: 'Malaysia' }, { code: 'MV', name: 'Maldives' }, { code: 'ML', name: 'Mali' },
  { code: 'MT', name: 'Malta' }, { code: 'MH', name: 'Marshall Islands' }, { code: 'MR', name: 'Mauritania' },
  { code: 'MU', name: 'Mauritius' }, { code: 'MX', name: 'Mexico' }, { code: 'FM', name: 'Micronesia' },
  { code: 'MD', name: 'Moldova' }, { code: 'MC', name: 'Monaco' }, { code: 'MN', name: 'Mongolia' },
  { code: 'ME', name: 'Montenegro' }, { code: 'MA', name: 'Morocco' }, { code: 'MZ', name: 'Mozambique' },
  { code: 'MM', name: 'Myanmar' }, { code: 'NA', name: 'Namibia' }, { code: 'NR', name: 'Nauru' },
  { code: 'NP', name: 'Nepal' }, { code: 'NL', name: 'Netherlands' }, { code: 'NZ', name: 'New Zealand' },
  { code: 'NI', name: 'Nicaragua' }, { code: 'NE', name: 'Niger' }, { code: 'NG', name: 'Nigeria' },
  { code: 'KP', name: 'North Korea' }, { code: 'MK', name: 'North Macedonia' }, { code: 'NO', name: 'Norway' },
  { code: 'OM', name: 'Oman' }, { code: 'PK', name: 'Pakistan' }, { code: 'PW', name: 'Palau' },
  { code: 'PS', name: 'Palestine' }, { code: 'PA', name: 'Panama' }, { code: 'PG', name: 'Papua New Guinea' },
  { code: 'PY', name: 'Paraguay' }, { code: 'PE', name: 'Peru' }, { code: 'PH', name: 'Philippines' },
  { code: 'PL', name: 'Poland' }, { code: 'PT', name: 'Portugal' }, { code: 'QA', name: 'Qatar' },
  { code: 'RO', name: 'Romania' }, { code: 'RU', name: 'Russia' }, { code: 'RW', name: 'Rwanda' },
  { code: 'KN', name: 'Saint Kitts and Nevis' }, { code: 'LC', name: 'Saint Lucia' },
  { code: 'VC', name: 'Saint Vincent and the Grenadines' }, { code: 'WS', name: 'Samoa' },
  { code: 'SM', name: 'San Marino' }, { code: 'ST', name: 'Sao Tome and Principe' },
  { code: 'SA', name: 'Saudi Arabia' }, { code: 'SN', name: 'Senegal' }, { code: 'RS', name: 'Serbia' },
  { code: 'SC', name: 'Seychelles' }, { code: 'SL', name: 'Sierra Leone' }, { code: 'SG', name: 'Singapore' },
  { code: 'SK', name: 'Slovakia' }, { code: 'SI', name: 'Slovenia' }, { code: 'SB', name: 'Solomon Islands' },
  { code: 'SO', name: 'Somalia' }, { code: 'ZA', name: 'South Africa' }, { code: 'KR', name: 'South Korea' },
  { code: 'SS', name: 'South Sudan' }, { code: 'ES', name: 'Spain' }, { code: 'LK', name: 'Sri Lanka' },
  { code: 'SD', name: 'Sudan' }, { code: 'SR', name: 'Suriname' }, { code: 'SE', name: 'Sweden' },
  { code: 'CH', name: 'Switzerland' }, { code: 'SY', name: 'Syria' }, { code: 'TW', name: 'Taiwan' },
  { code: 'TJ', name: 'Tajikistan' }, { code: 'TZ', name: 'Tanzania' }, { code: 'TH', name: 'Thailand' },
  { code: 'TL', name: 'Timor-Leste' }, { code: 'TG', name: 'Togo' }, { code: 'TO', name: 'Tonga' },
  { code: 'TT', name: 'Trinidad and Tobago' }, { code: 'TN', name: 'Tunisia' }, { code: 'TR', name: 'Turkey' },
  { code: 'TM', name: 'Turkmenistan' }, { code: 'TV', name: 'Tuvalu' }, { code: 'UG', name: 'Uganda' },
  { code: 'UA', name: 'Ukraine' }, { code: 'AE', name: 'United Arab Emirates' },
  { code: 'GB', name: 'United Kingdom' }, { code: 'US', name: 'United States' },
  { code: 'UY', name: 'Uruguay' }, { code: 'UZ', name: 'Uzbekistan' }, { code: 'VU', name: 'Vanuatu' },
  { code: 'VA', name: 'Vatican City' }, { code: 'VE', name: 'Venezuela' }, { code: 'VN', name: 'Vietnam' },
  { code: 'YE', name: 'Yemen' }, { code: 'ZM', name: 'Zambia' }, { code: 'ZW', name: 'Zimbabwe' }
];

const getCountryName = (code) => {
  if (!code) return '';
  const c = COUNTRIES.find(c => c.code === code.toUpperCase());
  return c ? c.name : code;
};

function CountrySelect({ value, onChange }) {
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtered = useMemo(() => {
    if (!search) return COUNTRIES;
    const q = search.toLowerCase();
    return COUNTRIES.filter(c => c.name.toLowerCase().includes(q) || c.code.toLowerCase().includes(q));
  }, [search]);

  const handleSelect = (code) => {
    onChange(code);
    setSearch('');
    setOpen(false);
  };

  const displayValue = open ? search : getCountryName(value) || '';

  return (
    <div className="country-select" ref={ref}>
      <input
        ref={inputRef}
        type="text"
        className="country-select-input"
        value={displayValue}
        placeholder="Search country…"
        onChange={e => { setSearch(e.target.value); if (!open) setOpen(true); }}
        onFocus={() => { setOpen(true); setSearch(''); }}
      />
      {value && !open && (
        <button className="country-select-clear" onClick={() => { onChange(''); inputRef.current?.focus(); }} aria-label="Clear">
          <i className="fa-solid fa-xmark"></i>
        </button>
      )}
      {open && (
        <ul className="country-select-dropdown">
          {filtered.length === 0 ? (
            <li className="country-select-empty">No countries found</li>
          ) : (
            filtered.map(c => (
              <li key={c.code} className={`country-select-option ${c.code === value ? 'selected' : ''}`} onMouseDown={() => handleSelect(c.code)}>
                <span className="country-select-name">{c.name}</span>
                <span className="country-select-code">{c.code}</span>
              </li>
            ))
          )}
        </ul>
      )}
    </div>
  );
}

function SubscriptionCard({ sub, onCancel, onStopCancel, onRetry, onChangePlan, onAccess, onCancelUpgrade, onUpdatePayment, isFailedPayment }) {
  const isTrialing   = sub.isTrialing || sub.status === 'trialing';
  const isPastDue    = sub.status === 'past_due';
  const trialPending = sub.trialPending;
  const svcStatus    = sub.serviceStatus;

  let statusLabel, statusClass;
  if (isPastDue) {
    statusLabel = 'Past Due'; statusClass = 'suspended';
  } else if (svcStatus === 'active' && isTrialing) {
    statusLabel = 'Trial Active'; statusClass = 'trial';
  } else if (svcStatus === 'active') {
    statusLabel = 'Active'; statusClass = 'active';
  } else if (svcStatus === 'pending' && sub.isLegacyDowngrade) {
    statusLabel = 'Downgrade Pending'; statusClass = 'pending';
  } else if (svcStatus === 'pending') {
    statusLabel = 'Pending Setup'; statusClass = 'pending';
  } else if (svcStatus === 'restoring') {
    statusLabel = 'Restoring'; statusClass = 'restoring';
  } else if (svcStatus === 'suspended') {
    statusLabel = 'Suspended'; statusClass = 'suspended';
  } else if (isTrialing && trialPending) {
    statusLabel = 'Awaiting Activation'; statusClass = 'pending';
  } else if (isTrialing) {
    statusLabel = 'Trial Active'; statusClass = 'trial';
  } else if (svcStatus === 'pending_upgrade') {
    statusLabel = sub.isBillingChange ? 'Billing Pending' : sub.isDowngrade ? 'Downgrade Pending' : 'Upgrade Pending'; statusClass = 'upgrade-pending';
  } else if (svcStatus === 'rejected') {
    statusLabel = 'Request Declined'; statusClass = 'suspended';
  } else {
    statusLabel = 'Pending'; statusClass = 'pending';
  }

  // Two distinct pending states
  const isAwaitingApproval = isTrialing && trialPending;
  const isLegacyDowngrade = !!sub.isLegacyDowngrade;
  const isProvisioning = svcStatus === 'pending' && !trialPending && !isLegacyDowngrade;
  const isPendingSetup = isAwaitingApproval || isProvisioning;

  // Lifetime deals (tagged via Stripe metadata.plan) have no recurring billing — show
  // "Lifetime" instead of Monthly/Yearly and hide the (placeholder $0) price entirely.
  const intervalLabel = sub.isLifetime ? 'Lifetime' : (sub.billingInterval === 'year' ? 'Yearly' : 'Monthly');
  const priceLabel = sub.isLifetime
    ? null
    : (sub.price != null
        ? `${sub.currency || 'USD'} ${Number(sub.price).toFixed(2)}/${sub.billingInterval === 'year' ? 'yr' : 'mo'}`
        : null);

  let dateInfo;
  if (isPendingSetup || svcStatus === 'rejected') {
    dateInfo = null;
  } else if (sub.cancelAtPeriodEnd) {
    dateInfo = { label: 'Cancels', value: new Date(sub.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }), warn: true };
  } else if (isTrialing && sub.trialEnd) {
    dateInfo = { label: 'Trial ends', value: new Date(sub.trialEnd * 1000).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) };
  } else if (sub.isLifetime) {
    // Lifetime deals don't renew — suppress the (misleading) renewal date.
    dateInfo = null;
  } else {
    dateInfo = { label: 'Renews', value: new Date(sub.endDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) };
  }

  return (
    <div className={`sub-card${isFailedPayment ? ' sub-card--failed' : ''}`}>
      <div className="sub-card-top">
        <div className="sub-card-identity">
          <div className="sub-card-icon">{getServiceName(sub.service).charAt(0)}</div>
          <div>
            <div className="sub-card-plan">{sub.planName}</div>
            <div className="sub-card-domain">{sub.domain || 'Domain not set'}</div>
          </div>
        </div>
        <span className={`sub-status-badge sub-status--${statusClass}`}>{statusLabel}</span>
      </div>

      {isLegacyDowngrade ? (
        <div className="sub-card-pending-box">
          <div className="sub-card-pending-icon">
            <i className="fa-solid fa-clock"></i>
          </div>
          <p className="sub-card-pending-title">Plan Downgrade Pending Review</p>
          <p className="sub-card-pending-msg">Your downgrade request is awaiting admin review. You'll receive an email once it's been processed.</p>
        </div>
      ) : isPendingSetup ? (
        <div className="sub-card-pending-box">
          {isAwaitingApproval ? (
            <>
              <div className="sub-card-pending-icon">
                <i className="fa-solid fa-clock"></i>
              </div>
              <p className="sub-card-pending-title">Trial Application Under Review</p>
              <p className="sub-card-pending-msg">Our team is reviewing your trial request. You'll receive an email once your instance is approved and ready to use.</p>
            </>
          ) : (
            <>
              <div className="sub-card-pending-steps">
                <div className="pending-step pending-step--done">
                  <i className="fa-solid fa-circle-check"></i>
                  <span>Approved</span>
                </div>
                <div className="pending-step pending-step--active">
                  <i className="fa-solid fa-spinner fa-spin"></i>
                  <span>Provisioning</span>
                </div>
                <div className="pending-step pending-step--upcoming">
                  <i className="fa-regular fa-circle"></i>
                  <span>Ready</span>
                </div>
              </div>
              <p className="sub-card-pending-msg">Your instance is being set up. You'll receive an email with access details shortly.</p>
            </>
          )}
        </div>
      ) : (
        <div className="sub-card-pills">
          {isTrialing && (
            <div className="sub-pill sub-pill--trial">
              <i className="fa-solid fa-flask"></i>
              {sub.trialDays > 0 ? `${sub.trialDays}-day trial` : 'Free trial'}
            </div>
          )}
          <div className="sub-pill">
            <i className="fa-solid fa-calendar-days"></i>
            {intervalLabel}
          </div>
          {priceLabel && (
            <div className="sub-pill">
              <i className="fa-solid fa-tag"></i>
              {priceLabel}
            </div>
          )}
          {isFailedPayment && (
            <div className="sub-pill sub-pill--warn">
              <i className="fa-solid fa-triangle-exclamation"></i>
              Payment failed
            </div>
          )}
        </div>
      )}

      {dateInfo && (
        <div className="sub-card-date" style={dateInfo.warn ? { color: '#f87171' } : {}}>
          {dateInfo.label}: <strong>{dateInfo.value}</strong>
        </div>
      )}
      {svcStatus === 'restoring' && (
        <div style={{ marginTop: '10px', padding: '10px 14px', background: 'rgba(16,185,129,0.08)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: '8px', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
          <i className="fa-solid fa-rotate fa-spin" style={{ color: '#10b981', fontSize: '14px', marginTop: '2px', flexShrink: 0 }}></i>
          <div>
            <p style={{ margin: '0 0 2px', color: '#10b981', fontSize: '13px', fontWeight: '600' }}>Payment received — service restoration in progress</p>
            <p style={{ margin: 0, color: '#6b7280', fontSize: '12px', lineHeight: '1.5' }}>
              Our team has been notified and will restore your service within 24 hours. You'll receive a confirmation email once it's back online.
            </p>
          </div>
        </div>
      )}
      {sub.isUpgradePending && (
        <div style={{ marginTop: '6px' }}>
          <div className="sub-card-date" style={{ color: '#f59e0b', fontSize: '12px' }}>
            <i className="fa-solid fa-clock" style={{ marginRight: 4 }}></i>
            {sub.isBillingChange ? 'Billing cycle change pending admin review' : sub.isDowngrade ? 'Plan downgrade pending admin review' : 'Plan upgrade pending admin review'}
          </div>
          {sub.proratedCredit > 0 && (
            <div className="sub-card-date" style={{ color: '#40E0D0', fontSize: '12px', marginTop: '4px' }}>
              <i className="fa-solid fa-receipt" style={{ marginRight: 4 }}></i>
              Credit from current plan: <strong>${(sub.proratedCredit / 100).toFixed(2)}</strong> applied to first invoice
            </div>
          )}
        </div>
      )}
      {sub.hasPendingUpgrade && (
        <div style={{ marginTop: '6px' }}>
          <div className="sub-card-date" style={{ color: '#f59e0b', fontSize: '12px' }}>
            <i className="fa-solid fa-clock" style={{ marginRight: 4 }}></i>
            {sub.pendingIsBillingChange ? 'Billing cycle change pending admin review' : sub.pendingIsDowngrade ? 'Plan downgrade pending admin review' : 'Plan upgrade pending admin review'}
          </div>
        </div>
      )}
      {sub.hasLegacyDowngrade && (
        <div style={{ marginTop: '6px' }}>
          <div className="sub-card-date" style={{ color: '#f59e0b', fontSize: '12px' }}>
            <i className="fa-solid fa-clock" style={{ marginRight: 4 }}></i>
            Plan downgrade pending admin review
          </div>
        </div>
      )}

      <div className="sub-card-actions">
        {isLegacyDowngrade ? (
          // Legacy downgrade (old flow): sub has service_status=pending + actionType=downgrade
          <button className="sub-btn sub-btn--ghost" onClick={() => onCancelUpgrade(sub)}>
            <i className="fa-solid fa-xmark"></i> Cancel Downgrade
          </button>
        ) : sub.hasLegacyDowngrade ? (
          // Active sub whose downgrade (legacy flow) is pending — hide Change Plan, offer cancel downgrade
          <>
            {sub.hasAccessFields && (
              <button className="sub-btn sub-btn--primary" onClick={() => onAccess(sub)}>
                <i className="fa-solid fa-key"></i> Access
              </button>
            )}
            <button className="sub-btn sub-btn--ghost" onClick={() => onCancelUpgrade({ id: sub.legacyDowngradeSubId })}>
              <i className="fa-solid fa-xmark"></i> Cancel Downgrade
            </button>
          </>
        ) : svcStatus === 'pending_upgrade' ? (
          // New pending-upgrade sub: only allow cancelling the change
          <button className="sub-btn sub-btn--ghost" onClick={() => onCancelUpgrade(sub)}>
            <i className="fa-solid fa-xmark"></i> {sub.isBillingChange ? 'Cancel Billing Change' : sub.isDowngrade ? 'Cancel Downgrade' : 'Cancel Upgrade'}
          </button>
        ) : sub.hasPendingUpgrade ? (
          // Old active sub with a pending plan change: show Access + Cancel Pending Change only
          <>
            {svcStatus === 'active' && sub.hasAccessFields && (
              <button className="sub-btn sub-btn--primary" onClick={() => onAccess(sub)}>
                <i className="fa-solid fa-key"></i> Access
              </button>
            )}
            <button className="sub-btn sub-btn--ghost" onClick={() => onCancelUpgrade({ id: sub.pendingUpgradeSubId })}>
              <i className="fa-solid fa-xmark"></i> {sub.pendingIsBillingChange ? 'Cancel Billing Change' : sub.pendingIsDowngrade ? 'Cancel Downgrade' : 'Cancel Upgrade'}
            </button>
          </>
        ) : svcStatus === 'rejected' ? (
          <a href="mailto:support@openhosst.com" className="sub-btn sub-btn--ghost">
            <i className="fa-solid fa-envelope"></i> Contact Support
          </a>
        ) : (
          <>
            {svcStatus === 'active' && sub.hasAccessFields && (
              <button className="sub-btn sub-btn--primary" onClick={() => onAccess(sub)}>
                <i className="fa-solid fa-key"></i> Access
              </button>
            )}
            {isFailedPayment && (
              <button className="sub-btn sub-btn--danger" onClick={() => onRetry(sub)}>
                <i className="fa-solid fa-rotate-right"></i> Retry Payment
              </button>
            )}
            {(isPastDue || svcStatus === 'suspended') && svcStatus !== 'restoring' && (
              <button className="sub-btn sub-btn--outline" onClick={() => onUpdatePayment(sub)}>
                <i className="fa-solid fa-credit-card"></i> Update Payment Method
              </button>
            )}
            {!isAwaitingApproval && (
              <button className="sub-btn sub-btn--outline" onClick={() => onChangePlan(sub)}>
                Change Plan
              </button>
            )}
            {!isAwaitingApproval && !sub.cancelAtPeriodEnd && (
              <button className="sub-btn sub-btn--ghost" onClick={() => onCancel(sub)}>
                Cancel
              </button>
            )}
            {!isAwaitingApproval && sub.cancelAtPeriodEnd && (
              <button className="sub-btn sub-btn--primary" onClick={() => onStopCancel(sub)}>
                <i className="fa-solid fa-rotate-left"></i> Stop Cancellation
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function AddAnotherCard({ to, serviceName }) {
  return (
    <Link to={to} className="add-instance-card">
      <div className="add-instance-icon">
        <i className="fa-solid fa-plus"></i>
      </div>
      <p className="add-instance-title">Add Another {serviceName}</p>
      <p className="add-instance-desc">Deploy a separate instance<br />with its own domain</p>
    </Link>
  );
}

export default function SubscriptionsPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [customerStatus, setCustomerStatus]   = useState(null);
  const [modal, setModal] = useState(null); // { title, message, type, actions }
  const closeModal = () => setModal(null);
  const [activeTab, setActiveTab]             = useState(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    return ['subscriptions', 'billing', 'invoices', 'support'].includes(tab) ? tab : 'subscriptions';
  });
  const [loading, setLoading]                 = useState(true);
  const [error, setError]                     = useState(null);
  const [failedSubscriptions, setFailedSubscriptions] = useState([]);
  const [search, setSearch]                   = useState('');
  const [serviceFilter, setServiceFilter]     = useState('all');
  const [statusFilter, setStatusFilter]       = useState('all');
  const [accountBalance, setAccountBalance]   = useState(null); // null = not loaded
  const [billingProfile, setBillingProfile]   = useState({ billingType: "personal", personalName: "", businessName: "", taxId: "", taxType: "", businessAddress: "", businessCity: "", businessState: "", businessZip: "", businessCountry: "" });
  const [billingSaving, setBillingSaving]     = useState(false);
  const [billingMsg, setBillingMsg]           = useState(null);
  const [invoices, setInvoices]               = useState([]);
  const [invoicesLoading, setInvoicesLoading] = useState(false);
  const [invoicesLoaded, setInvoicesLoaded]   = useState(false);
  // Support tickets state
  const [tickets, setTickets]                 = useState([]);
  const [ticketsLoading, setTicketsLoading]   = useState(false);
  const [ticketsLoaded, setTicketsLoaded]     = useState(false);
  const [selectedTicket, setSelectedTicket]   = useState(null);
  const [ticketReplies, setTicketReplies]     = useState([]);
  const [showNewTicketForm, setShowNewTicketForm] = useState(false);
  const [newTicket, setNewTicket]             = useState({ subject: '', category: 'general', subscription_id: '', message: '' });
  const [replyMessage, setReplyMessage]       = useState('');
  const [ticketStatusFilter, setTicketStatusFilter] = useState('all');
  const [ticketSubmitting, setTicketSubmitting] = useState(false);
  const [ticketAttachments, setTicketAttachments] = useState([]);
  const [replyAttachments, setReplyAttachments] = useState([]);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [replySending, setReplySending] = useState(false);
  const chatBottomRef = useRef(null);
  const chatMessagesRef = useRef(null);
  const pollRef = useRef(null);
  const [typingIndicator, setTypingIndicator] = useState(false);
  const typingTimerRef = useRef(null);
  const typingDebounceRef = useRef(null);
  const [supportStatus, setSupportStatus] = useState('online');

  const user = getUser();
  const userEmail = user?.email || '';

  useEffect(() => {
    if (!isAuthenticated()) { navigate('/login', { replace: true }); return; }
    if (!isEmailVerified())  { navigate('/verify-email', { replace: true }); return; }
    loadData();
  }, []);

  // Auto-reload data when SSE reconnects (handles server restarts, network drops)
  useEffect(() => {
    const onReconnect = () => { loadData(); };
    window.addEventListener('openhosst:sse-reconnect', onReconnect);
    return () => window.removeEventListener('openhosst:sse-reconnect', onReconnect);
  }, []);

  const loadData = async () => {
    // Load support status early so the tab indicator is correct
    axios.get(`${API_URL}/api/support/status`).then(r => setSupportStatus(r.data.status || 'online')).catch(() => {});
    try {
      setLoading(true); setError(null);
      const res = await axios.post(`${API_URL}/api/check-customer`, {}, getAuthConfig());
      setCustomerStatus(res.data);
      try {
        const fr = await axios.get(`${API_URL}/api/subscriptions/failed`, getAuthConfig());
        setFailedSubscriptions(fr.data.failedSubscriptions || []);
      } catch { setFailedSubscriptions([]); }
      try {
        const balRes = await axios.get(`${API_URL}/api/subscriptions/account-balance`, getAuthConfig());
        setAccountBalance(balRes.data.balanceCents || 0);
      } catch { setAccountBalance(0); }
    } catch { setError('Failed to load subscriptions. Please try again.'); }
    finally  { setLoading(false); }
  };

  const handleLogout     = () => { logout(); navigate('/login', { replace: true }); };
  const handleCancel     = (sub) => navigate(`/${sub.service || 'n8n'}/cancel-subscription`, { state: { subscription: sub } });
  const handleStopCancel = (sub) => {
    setModal({
      type: 'info',
      title: 'Resume subscription?',
      message: 'This subscription will no longer be cancelled at the end of the billing period.',
      onConfirm: async () => {
        try {
          await axios.post(`${API_URL}/api/subscriptions/resume`, { subscriptionId: sub.id }, getAuthConfig());
          await loadData();
        } catch (err) {
          setModal({ type: 'error', title: 'Error', message: err.response?.data?.error || 'Failed to resume subscription.' });
        }
      },
      confirmLabel: 'Resume',
    });
  };
  const handleRetry         = (sub) => navigate(`/${sub.service || 'n8n'}/retry-payment`,        { state: { subscription: sub } });
  const handleUpdatePayment = (sub) => navigate('/update-payment-method', { state: { subscription: sub } });
  const handleChangePlan = (sub) => {
    // Block plan change if any subscription already has a pending plan change awaiting approval
    const hasPending = allSubs.some(s => s.serviceStatus === 'pending_upgrade' || s.hasPendingUpgrade || s.isLegacyDowngrade || s.hasLegacyDowngrade);
    if (hasPending) {
      setModal({
        type: 'warning',
        title: 'Plan change pending approval',
        message: 'You already have a plan change pending admin review. Please wait for it to be approved or cancelled before submitting another.',
      });
      return;
    }
    navigate(`/${sub.service || 'n8n'}`, { state: { changePlanSub: sub } });
  };
  const handleAccess    = (sub) => navigate(`/subscriptions/${sub.id}/access`);
  const handleCancelUpgrade = (sub) => {
    setModal({
      type: 'warning',
      title: 'Cancel pending change?',
      message: 'Your current subscription will remain active and the pending plan change will be removed.',
      onConfirm: async () => {
        try {
          await axios.post(`${API_URL}/api/subscriptions/cancel-upgrade`, { subscriptionId: sub.id }, getAuthConfig());
          loadData();
        } catch (err) {
          setModal({ type: 'error', title: 'Error', message: err.response?.data?.error || 'Failed to cancel plan change. Please try again.' });
        }
      },
      confirmLabel: 'Yes, Cancel Change',
    });
  };

  // Load business profile when Billing tab is selected
  const loadBillingProfile = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/business-profile`, getAuthConfig());
      if (res.data.profile) {
        const p = res.data.profile;
        setBillingProfile({
          billingType: p.billing_type || 'personal',
          personalName: p.business_name || user?.fullName || '',
          businessName: p.business_name || '',
          taxId: p.tax_id || '',
          taxType: p.tax_type || (() => {
            const v = (p.tax_id || '').replace(/\s/g, '').toUpperCase();
            if (!v) return '';
            if (/^GB\d{1,12}$/.test(v)) return 'VAT (UK)';
            if (/^(AT|BE|BG|CY|CZ|DE|DK|EE|EL|ES|FI|FR|HR|HU|IE|IT|LT|LU|LV|MT|NL|PL|PT|RO|SE|SI|SK)[A-Z0-9]*$/.test(v) && v.length >= 3) return 'VAT (EU)';
            if (/^\d{2}-?\d{0,7}$/.test(v) && v.includes('-')) return 'EIN (US)';
            if (/^\d{2}[A-Z]/.test(v) && v.length >= 5) return 'GST (IN)';
            return '';
          })(),
          businessAddress: p.business_address || '',
          businessCity: p.business_city || '',
          businessState: p.business_state || '',
          businessZip: p.business_zip || '',
          businessCountry: p.business_country || ''
        });
      } else {
        setBillingProfile(p => ({ ...p, personalName: user?.fullName || '' }));
      }
    } catch (e) { console.error('Failed to load business profile:', e); }
  };

  const saveBillingProfile = async () => {
    setBillingSaving(true); setBillingMsg(null);
    try {
      const resp = await axios.put(`${API_URL}/api/business-profile`, billingProfile, getAuthConfig());
      const warnings = resp.data.warnings || [];
      if (warnings.length > 0) {
        setBillingMsg({ type: 'warning', text: 'Details saved. ' + warnings.join(' ') });
      } else {
        setBillingMsg({ type: 'success', text: 'Billing details saved. These details will appear on your future invoices.' });
      }
    } catch (e) {
      setBillingMsg({ type: 'error', text: e.response?.data?.error || 'Failed to save billing details. Please try again.' });
    } finally { setBillingSaving(false); }
  };

  const loadInvoices = async () => {
    if (invoicesLoaded) return;
    setInvoicesLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/invoices`, getAuthConfig());
      setInvoices(res.data.invoices || []);
      setInvoicesLoaded(true);
    } catch (e) { console.error('Failed to load invoices:', e); }
    finally { setInvoicesLoading(false); }
  };

  const loadTickets = async () => {
    if (ticketsLoaded) return;
    setTicketsLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/support/tickets`, getAuthConfig());
      setTickets(res.data.tickets || []);
      setTicketsLoaded(true);
    } catch (e) { console.error('Failed to load tickets:', e); }
    finally { setTicketsLoading(false); }
  };

  const scrollToBottomIfNear = () => {
    const el = chatMessagesRef.current;
    if (!el) return;
    const distFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
    if (distFromBottom < 120) chatBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchReplies = useCallback(async (id) => {
    try {
      const res = await axios.get(`${API_URL}/api/support/tickets/${id}`, getAuthConfig());
      setTicketReplies(res.data.replies || []);
      // Preserve local email toggle state — don't overwrite with server value during poll
      setSelectedTicket(prev => prev ? { ...res.data.ticket, user_email_notifications: prev.user_email_notifications } : res.data.ticket);
      // Mark admin messages as read so the admin sees the double tick immediately
      axios.post(`${API_URL}/api/support/tickets/${id}/read`, {}, getAuthConfig()).catch(() => {});
    } catch {}
  }, []);

  const loadTicketDetail = useCallback(async (id) => {
    try {
      const res = await axios.get(`${API_URL}/api/support/tickets/${id}`, getAuthConfig());
      setSelectedTicket(res.data.ticket);
      setTicketReplies(res.data.replies || []);
      setTimeout(() => chatBottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 80);
      axios.post(`${API_URL}/api/support/tickets/${id}/read`, {}, getAuthConfig()).catch(() => {});
      axios.get(`${API_URL}/api/support/status`).then(r => setSupportStatus(r.data.status || 'online')).catch(() => {});
    } catch (e) { console.error('Failed to load ticket:', e); }
  }, []);

  // Track whether the user is near the bottom via scroll events (captured before DOM changes)
  const isAtBottomRef = useRef(true);
  useEffect(() => {
    const el = chatMessagesRef.current;
    if (!el) return;
    const onScroll = () => {
      isAtBottomRef.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [selectedTicket?.id]);

  // Auto-scroll when a new message arrives — only if user was already at the bottom
  const prevReplyCountRef = useRef(0);
  useEffect(() => {
    const newCount = ticketReplies.length;
    if (newCount > prevReplyCountRef.current && isAtBottomRef.current) {
      setTimeout(() => chatBottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 30);
    }
    prevReplyCountRef.current = newCount;
  }, [ticketReplies.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep a ref of the current ticket ID so event listeners can read it without stale closures
  const selectedTicketIdRef = useRef(null);
  useEffect(() => { selectedTicketIdRef.current = selectedTicket?.id || null; }, [selectedTicket?.id]);

  // Hide floating support button on mobile when ticket chat is open (overlaps input bar)
  useEffect(() => {
    if (selectedTicket) document.body.classList.add('ticket-chat-open');
    else document.body.classList.remove('ticket-chat-open');
    return () => document.body.classList.remove('ticket-chat-open');
  }, [selectedTicket]);

  // Auto-grow textarea
  const replyTextareaRef = useRef(null);
  useEffect(() => {
    const el = replyTextareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 120) + 'px';
  }, [replyMessage]);

  // Poll every 10s + react instantly to SSE notification events
  useEffect(() => {
    if (!selectedTicket) {
      clearInterval(pollRef.current);
      return;
    }
    clearInterval(pollRef.current);
    pollRef.current = setInterval(() => {
      if (selectedTicketIdRef.current) fetchReplies(selectedTicketIdRef.current);
    }, 10000);

    const onNotif = (e) => {
      const d = e.detail;
      if (!d) return;
      if (d.event === 'typing' && d.ticketId === selectedTicketIdRef.current) {
        setTypingIndicator(true);
        clearTimeout(typingTimerRef.current);
        typingTimerRef.current = setTimeout(() => setTypingIndicator(false), 3500);
      }
      if (d.event === 'read' && d.ticketId === selectedTicketIdRef.current) {
        fetchReplies(selectedTicketIdRef.current);
      }
      if (d.event === 'support_status') setSupportStatus(d.status);
      const type = d?.type || '';
      if ((type === 'ticket_admin_reply' || type === 'ticket_reply') && selectedTicketIdRef.current) {
        fetchReplies(selectedTicketIdRef.current);
      }
    };
    window.addEventListener('openhosst:notification', onNotif);
    return () => {
      clearInterval(pollRef.current);
      window.removeEventListener('openhosst:notification', onNotif);
    };
  }, [selectedTicket?.id, fetchReplies]);

  // Push registration is handled globally in AppNav

  const handleCreateTicket = async (e) => {
    e.preventDefault();
    if (!newTicket.subject.trim() || !newTicket.message.trim()) return;
    if (hasAnySubscription && !newTicket.subscription_id) return;
    setTicketSubmitting(true);
    try {
      const fd = new FormData();
      fd.append('subject', newTicket.subject);
      fd.append('category', newTicket.category);
      fd.append('subscription_id', newTicket.subscription_id);
      fd.append('message', newTicket.message);
      ticketAttachments.forEach(f => fd.append('attachments', f));
      await axios.post(`${API_URL}/api/support/tickets`, fd, { headers: { Authorization: getAuthConfig().headers.Authorization } });
      setNewTicket({ subject: '', category: 'general', subscription_id: '', message: '' });
      setTicketAttachments([]);
      setShowNewTicketForm(false);
      const res = await axios.get(`${API_URL}/api/support/tickets`, getAuthConfig());
      setTickets(res.data.tickets || []);
      setTicketsLoaded(true);
    } catch (e) { console.error('Failed to create ticket:', e); }
    finally { setTicketSubmitting(false); }
  };

  const handleTicketReply = async () => {
    if ((!replyMessage.trim() && replyAttachments.length === 0) || !selectedTicket || replySending) return;
    const msgText = replyMessage;
    const msgAttachments = replyAttachments;

    // Optimistic update — add message instantly
    const optimisticId = `opt-${Date.now()}`;
    const optimisticMsg = {
      id: optimisticId,
      sender_type: 'customer',
      message: msgText,
      attachments: [],
      created_at: new Date().toISOString(),
      read_at: null,
      _optimistic: true,
    };
    setTicketReplies(prev => [...prev, optimisticMsg]);
    setReplyMessage('');
    setReplyAttachments([]);
    setTimeout(() => chatBottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 30);

    setReplySending(true);
    try {
      const fd = new FormData();
      fd.append('message', msgText);
      msgAttachments.forEach(f => fd.append('attachments', f));
      await axios.post(`${API_URL}/api/support/tickets/${selectedTicket.id}/replies`, fd, { headers: { Authorization: getAuthConfig().headers.Authorization } });
      // Replace optimistic with real data
      await fetchReplies(selectedTicket.id);
    } catch (e) {
      console.error('Failed to send reply:', e);
      // Remove optimistic message on failure and restore input
      setTicketReplies(prev => prev.filter(r => r.id !== optimisticId));
      setReplyMessage(msgText);
      setReplyAttachments(msgAttachments);
    }
    finally { setReplySending(false); }
  };

  const handleReplyKeyDown = (e) => {
    if (e.key === 'Enter' && (e.ctrlKey || e.metaKey)) { e.preventDefault(); handleTicketReply(); }
  };

  const handleDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); };
  const handleDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); };
  const handleDrop = (e, setter) => {
    e.preventDefault(); e.stopPropagation(); setDragActive(false);
    const files = [...e.dataTransfer.files].filter(f => /^image\/(png|jpe?g|gif|webp)$/.test(f.type)).slice(0, 5);
    setter(prev => [...prev, ...files].slice(0, 5));
  };
  const handleFileSelect = (e, setter) => {
    const files = [...e.target.files].filter(f => /^image\/(png|jpe?g|gif|webp)$/.test(f.type)).slice(0, 5);
    setter(prev => [...prev, ...files].slice(0, 5));
    e.target.value = '';
  };
  const removeAttachment = (idx, setter) => { setter(prev => prev.filter((_, i) => i !== idx)); };

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    if (tab === 'billing') loadBillingProfile();
    if (tab === 'invoices') loadInvoices();
    if (tab === 'support') loadTickets();
  };

  // Sync active tab and deep-link ticket from URL — runs on mount AND on URL changes
  // (handles notification clicks when user is already on this page)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tab = params.get('tab');
    const ticketId = params.get('ticket');
    if (tab && ['subscriptions', 'billing', 'invoices', 'support'].includes(tab)) {
      setActiveTab(tab);
    }
    if (tab === 'support') {
      loadTickets();
      if (ticketId) loadTicketDetail(parseInt(ticketId, 10));
    } else if (tab === 'billing') loadBillingProfile();
    else if (tab === 'invoices') loadInvoices();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.search]);

  const allSubs           = customerStatus?.activeSubscriptions || [];
  const incompleteCheckouts = customerStatus?.incompleteCheckouts || [];
  const failedSubIds = new Set(failedSubscriptions.map(s => s.id));

  // Hide pending_upgrade cards whose active counterpart is visible (avoids duplicate cards)
  const pendingUpgradeIdsWithActiveCounterpart = useMemo(() => {
    const s = new Set();
    allSubs.forEach(sub => { if (sub.hasPendingUpgrade && sub.pendingUpgradeSubId) s.add(sub.pendingUpgradeSubId); });
    return s;
  }, [allSubs]);
  // Hide legacy downgrade subs when an active sub covers the same service+domain
  const activeSvcDomainPairs = useMemo(() =>
    new Set(allSubs.filter(s => s.serviceStatus === 'active' && !s.isLegacyDowngrade).map(s => `${s.service}:${s.domain}`))
  , [allSubs]);

  const displaySubs = useMemo(() =>
    allSubs.filter(s => {
      if (s.isUpgradePending && pendingUpgradeIdsWithActiveCounterpart.has(s.id)) return false;
      if (s.isLegacyDowngrade && activeSvcDomainPairs.has(`${s.service}:${s.domain}`)) return false;
      return true;
    })
  , [allSubs, pendingUpgradeIdsWithActiveCounterpart, activeSvcDomainPairs]);

  const serviceOptions = useMemo(() =>
    [...new Set(displaySubs.map(s => s.service || 'other'))].sort(), [displaySubs]);

  const filtered = useMemo(() => {
    let list = displaySubs;
    if (serviceFilter !== 'all') list = list.filter(s => (s.service || 'other') === serviceFilter);
    if (statusFilter === 'active')    list = list.filter(s => s.serviceStatus === 'active' && !s.isTrialing);
    else if (statusFilter === 'trial')     list = list.filter(s => s.isTrialing || s.status === 'trialing');
    else if (statusFilter === 'pending')   list = list.filter(s => (s.serviceStatus === 'pending' || (!s.serviceStatus && !s.isTrialing)) && s.serviceStatus !== 'rejected');
    else if (statusFilter === 'past_due')   list = list.filter(s => s.status === 'past_due');
    else if (statusFilter === 'suspended') list = list.filter(s => s.serviceStatus === 'suspended' || s.serviceStatus === 'restoring');
    else if (statusFilter === 'failed')    list = list.filter(s => failedSubIds.has(s.id));
    if (search.trim()) {
      const q = search.trim().toLowerCase();
      list = list.filter(s =>
        (s.planName || '').toLowerCase().includes(q) ||
        (s.domain   || '').toLowerCase().includes(q) ||
        getServiceName(s.service).toLowerCase().includes(q)
      );
    }
    return list;
  }, [allSubs, serviceFilter, statusFilter, search, failedSubIds]);

  const grouped = useMemo(() => {
    const map = {};
    filtered.forEach(s => { const k = s.service || 'other'; if (!map[k]) map[k] = []; map[k].push(s); });
    return map;
  }, [filtered]);

  if (loading) return (
    <div className="checkout-container-new">
      <AppNav onLogout={handleLogout} />
      <div className="loading-container"><div className="loading-spinner"></div></div>
      <p className="loading-text">Loading your subscriptions...</p>
    </div>
  );

  const cancelledHistory = customerStatus?.cancelledHistory || [];
  const hasAnySubscription = displaySubs.length > 0 || cancelledHistory.length > 0;

  const statusCounts = {
    active:    displaySubs.filter(s => s.serviceStatus === 'active' && !s.isTrialing && s.status !== 'past_due').length,
    trial:     displaySubs.filter(s => s.isTrialing || s.status === 'trialing').length,
    pending:   displaySubs.filter(s => (s.serviceStatus === 'pending' || (!s.serviceStatus && !s.isTrialing)) && s.serviceStatus !== 'rejected').length,
    suspended: displaySubs.filter(s => s.serviceStatus === 'suspended' || s.serviceStatus === 'restoring').length,
    pastDue:   displaySubs.filter(s => s.status === 'past_due').length,
    failed:    failedSubscriptions.length,
  };

  return (
    <div className="checkout-container-new">
      {modal && (
        <BrandedModal
          title={modal.title}
          message={modal.message}
          type={modal.type}
          onClose={closeModal}
          onConfirm={modal.onConfirm}
          confirmLabel={modal.confirmLabel}
        />
      )}
      <AppNav onLogout={handleLogout} />
      <div className="subscriptions-page">

        <header className="subs-page-header">
          <h1>My Subscriptions</h1>
          {userEmail && <p className="subs-page-email">{userEmail}</p>}
        </header>

        {error && <div className="alert alert-error" style={{ marginBottom: '24px' }}>{error}</div>}

        <nav className="subs-page-tabs">
          {[
            { key: 'subscriptions', label: `Subscriptions${displaySubs.length > 0 ? ` (${displaySubs.length})` : ''}` },
            { key: 'billing',  label: 'Billing' },
            { key: 'invoices', label: 'Invoices' },
            { key: 'support', label: (
              <span style={{display:'inline-flex',alignItems:'center',gap:'6px'}}>
                Support
                <span style={{
                  width:'7px',height:'7px',borderRadius:'50%',display:'inline-block',flexShrink:0,
                  background: supportStatus==='online'?'#10b981':supportStatus==='away'?'#f59e0b':supportStatus==='busy'?'#f97316':'#6b7280',
                }} title={`Support is ${supportStatus}`}/>
              </span>
            )},
          ].map(t => (
            <button key={t.key} className={`subs-tab-btn ${activeTab === t.key ? 'active' : ''}`} onClick={() => handleTabChange(t.key)}>
              {t.label}
            </button>
          ))}
        </nav>

        {activeTab === 'subscriptions' && (
          <div>
            {accountBalance !== null && accountBalance < 0 && (
              <div style={{ background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.12), rgba(64, 224, 208, 0.08))', border: '1px solid rgba(16, 185, 129, 0.3)', borderRadius: '10px', padding: '14px 18px', marginBottom: '16px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '4px' }}>
                  <i className="fa-solid fa-wallet" style={{ color: '#10b981', fontSize: '18px' }}></i>
                  <span style={{ color: '#fff', fontSize: '15px', fontWeight: '600' }}>
                    Account Credit: <span style={{ color: '#40E0D0' }}>${(Math.abs(accountBalance) / 100).toFixed(2)}</span>
                  </span>
                </div>
                <p style={{ margin: 0, paddingLeft: '28px', color: '#9ca3af', fontSize: '12px' }}>
                  This credit will automatically be applied to your upcoming invoices.
                </p>
              </div>
            )}
            {incompleteCheckouts.length > 0 && (
              <div style={{ marginBottom: '16px' }}>
                {incompleteCheckouts.map(ic => {
                  const expiresIn = Math.max(0, Math.round((new Date(ic.expiresAt) - Date.now()) / 60000));
                  const hoursLeft = Math.floor(expiresIn / 60);
                  const minsLeft  = expiresIn % 60;
                  const expiryLabel = hoursLeft > 0 ? `${hoursLeft}h ${minsLeft}m` : `${minsLeft}m`;
                  return (
                    <div key={ic.id} style={{
                      background: 'linear-gradient(135deg, rgba(245, 158, 11, 0.12), rgba(251, 191, 36, 0.06))',
                      border: '1px solid rgba(245, 158, 11, 0.35)',
                      borderRadius: '10px', padding: '14px 18px', marginBottom: '10px',
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '12px', flexWrap: 'wrap'
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                        <i className="fa-solid fa-clock" style={{ color: '#f59e0b', fontSize: '20px', flexShrink: 0 }}></i>
                        <div style={{ minWidth: 0 }}>
                          <p style={{ margin: 0, color: '#fff', fontSize: '14px', fontWeight: '600' }}>
                            Incomplete checkout — {ic.planName}{ic.domain ? ` · ${ic.domain}` : ''}
                          </p>
                          <p style={{ margin: '2px 0 0', color: '#9ca3af', fontSize: '12px' }}>
                            Payment not completed · expires in {expiryLabel}
                          </p>
                        </div>
                      </div>
                      <button
                        className="sub-btn sub-btn--primary"
                        style={{ flexShrink: 0, fontSize: '13px', padding: '8px 16px' }}
                        onClick={() => navigate(`/${ic.service}`, { state: { resumeIncomplete: ic } })}
                      >
                        <i className="fa-solid fa-arrow-right"></i> Complete Checkout
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
            {displaySubs.length > 0 && (
              <div className="subs-toolbar">
                {/* Search bar */}
                <div className="subs-search-wrap">
                  <i className="fa-solid fa-magnifying-glass subs-search-icon"></i>
                  <input
                    type="text"
                    className="subs-search-input"
                    placeholder="Search by plan, domain or service…"
                    value={search}
                    onChange={e => setSearch(e.target.value)}
                  />
                  {search && (
                    <button className="subs-search-clear" onClick={() => setSearch('')} aria-label="Clear search">
                      <i className="fa-solid fa-xmark"></i>
                    </button>
                  )}
                </div>

                {/* Service filter — only shown when user has multiple services */}
                {serviceOptions.length > 1 && (
                  <div className="subs-filter-row">
                    <span className="subs-filter-label">Service</span>
                    <div className="subs-filter-chips">
                      <button className={`subs-chip ${serviceFilter === 'all' ? 'subs-chip--active' : ''}`} onClick={() => setServiceFilter('all')}>
                        All
                      </button>
                      {serviceOptions.map(svc => (
                        <button key={svc} className={`subs-chip ${serviceFilter === svc ? 'subs-chip--active' : ''}`} onClick={() => setServiceFilter(svc)}>
                          {getServiceName(svc)}
                          <span className="subs-chip-count">{displaySubs.filter(s => (s.service || 'other') === svc).length}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}

                {/* Status filter */}
                <div className="subs-filter-row">
                  <span className="subs-filter-label">Status</span>
                  <div className="subs-filter-chips">
                    <button className={`subs-chip ${statusFilter === 'all' ? 'subs-chip--active' : ''}`} onClick={() => setStatusFilter('all')}>
                      All
                    </button>
                    {statusCounts.active > 0 && (
                      <button className={`subs-chip ${statusFilter === 'active' ? 'subs-chip--active' : ''}`} onClick={() => setStatusFilter('active')}>
                        Active <span className="subs-chip-count">{statusCounts.active}</span>
                      </button>
                    )}
                    {statusCounts.trial > 0 && (
                      <button className={`subs-chip subs-chip--trial-opt ${statusFilter === 'trial' ? 'subs-chip--active' : ''}`} onClick={() => setStatusFilter('trial')}>
                        Trial <span className="subs-chip-count">{statusCounts.trial}</span>
                      </button>
                    )}
                    {statusCounts.pending > 0 && (
                      <button className={`subs-chip ${statusFilter === 'pending' ? 'subs-chip--active' : ''}`} onClick={() => setStatusFilter('pending')}>
                        Pending <span className="subs-chip-count">{statusCounts.pending}</span>
                      </button>
                    )}
                    {statusCounts.pastDue > 0 && (
                      <button className={`subs-chip subs-chip--danger-opt ${statusFilter === 'past_due' ? 'subs-chip--active' : ''}`} onClick={() => setStatusFilter('past_due')}>
                        Past Due <span className="subs-chip-count">{statusCounts.pastDue}</span>
                      </button>
                    )}
                    {statusCounts.suspended > 0 && (
                      <button className={`subs-chip subs-chip--danger-opt ${statusFilter === 'suspended' ? 'subs-chip--active' : ''}`} onClick={() => setStatusFilter('suspended')}>
                        Suspended <span className="subs-chip-count">{statusCounts.suspended}</span>
                      </button>
                    )}
                    {statusCounts.failed > 0 && (
                      <button className={`subs-chip subs-chip--danger-opt ${statusFilter === 'failed' ? 'subs-chip--active' : ''}`} onClick={() => setStatusFilter('failed')}>
                        Failed <span className="subs-chip-count">{statusCounts.failed}</span>
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}

            {filtered.length > 0 ? (
              Object.entries(grouped).map(([svcKey, subs]) => (
                <div key={svcKey} className="service-group">
                  <div className="service-group-header">
                    <h2>
                      {getServiceName(svcKey)}
                      <span className="subs-group-count">{subs.length}</span>
                    </h2>
                    <Link to={`/${svcKey}`} className="add-service-btn">
                    <i className="fa-solid fa-plus" style={{ fontSize: '11px' }}></i>
                    New {getServiceName(svcKey)} Instance
                  </Link>
                  </div>
                  <div className="subs-cards-grid">
                    {subs.map(sub => (
                      <SubscriptionCard
                        key={sub.id}
                        sub={sub}
                        onCancel={handleCancel}
                        onStopCancel={handleStopCancel}
                        onRetry={handleRetry}
                        onUpdatePayment={handleUpdatePayment}
                        onChangePlan={handleChangePlan}
                        onAccess={handleAccess}
                        onCancelUpgrade={handleCancelUpgrade}
                        isFailedPayment={failedSubIds.has(sub.id)}
                      />
                    ))}
                    <AddAnotherCard to={`/${svcKey}`} serviceName={getServiceName(svcKey)} />
                  </div>
                </div>
              ))
            ) : displaySubs.length === 0 ? (
              <div className="subs-page-empty">
                <h3>No subscriptions yet</h3>
                <p>Browse our App Store to get started.</p>
                <Link to="/apps" className="btn-subscribe" style={{ display: 'inline-block', marginTop: '8px', textDecoration: 'none', padding: '12px 28px' }}>
                  Browse App Store <i className="fa-solid fa-arrow-right" style={{marginLeft:4}}></i>
                </Link>
              </div>
            ) : (
              <div className="subs-no-results">
                <i className="fa-solid fa-filter-circle-xmark"></i>
                <p>No subscriptions match your filters.</p>
                <button onClick={() => { setSearch(''); setServiceFilter('all'); setStatusFilter('all'); }} className="subs-clear-filters">
                  Clear filters
                </button>
              </div>
            )}

            {/* Cancelled subscription history */}
            {cancelledHistory.length > 0 && statusFilter === 'all' && !search.trim() && (
              <div className="subs-history-section">
                <h3 className="subs-history-title">
                  <i className="fa-solid fa-clock-rotate-left"></i> Subscription History
                </h3>
                <div className="subs-cards-grid">
                  {cancelledHistory.map(sub => (
                    <div key={sub.id} className="subscription-card sub-card--cancelled">
                      <div className="sub-card-header">
                        <div className="sub-card-info">
                          <p className="sub-plan-name">{sub.planName}</p>
                          {sub.domain && <p className="sub-domain">{sub.domain}</p>}
                        </div>
                        <span className="sub-status-badge sub-status--cancelled">Cancelled</span>
                      </div>
                      {sub.canceledAt && (
                        <p className="sub-cancelled-date">
                          <i className="fa-solid fa-ban"></i> Cancelled {new Date(sub.canceledAt).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === 'billing' && (
          <div className="billing-tab">
            <div className="billing-form-card">
              <h3 className="billing-form-title">
                <i className="fa-solid fa-file-invoice"></i> Billing Details
              </h3>
              <p className="billing-form-desc">These details will appear on your invoices. Activate either Personal or Business billing below.</p>

              {billingMsg && (
                <div className={`billing-msg billing-msg--${billingMsg.type}`}>{billingMsg.text}</div>
              )}

              {/* ── Personal Details Section ── */}
              <div className={`billing-section ${billingProfile.billingType === 'personal' ? 'billing-section--active' : 'billing-section--inactive'}`}>
                <div className="billing-section-header" onClick={() => billingProfile.billingType !== 'personal' && setBillingProfile(p => ({ ...p, billingType: 'personal' }))}>
                  <div className="billing-section-label">
                    <div className={`billing-section-radio ${billingProfile.billingType === 'personal' ? 'billing-section-radio--selected' : ''}`}>
                      {billingProfile.billingType === 'personal' && <i className="fa-solid fa-check"></i>}
                    </div>
                    <div>
                      <h4><i className="fa-solid fa-user"></i> Personal</h4>
                      <p>Invoice under your personal name and address</p>
                    </div>
                  </div>
                  {billingProfile.billingType !== 'personal' && (
                    <button className="billing-activate-btn" onClick={(e) => { e.stopPropagation(); setBillingProfile(p => ({ ...p, billingType: 'personal' })); }}>
                      <i className="fa-solid fa-toggle-off"></i> Activate
                    </button>
                  )}
                  {billingProfile.billingType === 'personal' && (
                    <span className="billing-active-badge"><i className="fa-solid fa-circle-check"></i> Active</span>
                  )}
                </div>
                {billingProfile.billingType !== 'personal' && <div className="billing-section-overlay"></div>}
                <div className="billing-section-body">
                  <div className="billing-form-grid">
                    <div className="billing-field billing-field--full">
                      <label>Full Name</label>
                      <input type="text" value={billingProfile.personalName} onChange={e => setBillingProfile(p => ({ ...p, personalName: e.target.value }))} placeholder="John Doe" disabled={billingProfile.billingType !== 'personal'} />
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Business Details Section ── */}
              <div className={`billing-section ${billingProfile.billingType === 'business' ? 'billing-section--active' : 'billing-section--inactive'}`}>
                <div className="billing-section-header" onClick={() => billingProfile.billingType !== 'business' && setBillingProfile(p => ({ ...p, billingType: 'business' }))}>
                  <div className="billing-section-label">
                    <div className={`billing-section-radio ${billingProfile.billingType === 'business' ? 'billing-section-radio--selected' : ''}`}>
                      {billingProfile.billingType === 'business' && <i className="fa-solid fa-check"></i>}
                    </div>
                    <div>
                      <h4><i className="fa-solid fa-building"></i> Business</h4>
                      <p>Invoice under your company name, address, and tax ID</p>
                    </div>
                  </div>
                  {billingProfile.billingType !== 'business' && (
                    <button className="billing-activate-btn" onClick={(e) => { e.stopPropagation(); setBillingProfile(p => ({ ...p, billingType: 'business' })); }}>
                      <i className="fa-solid fa-toggle-off"></i> Activate
                    </button>
                  )}
                  {billingProfile.billingType === 'business' && (
                    <span className="billing-active-badge"><i className="fa-solid fa-circle-check"></i> Active</span>
                  )}
                </div>
                {billingProfile.billingType !== 'business' && <div className="billing-section-overlay"></div>}
                <div className="billing-section-body">
                  <div className="billing-form-grid">
                    <div className="billing-field billing-field--full">
                      <label>Business Name</label>
                      <input type="text" value={billingProfile.businessName} onChange={e => setBillingProfile(p => ({ ...p, businessName: e.target.value }))} placeholder="Acme Corp" disabled={billingProfile.billingType !== 'business'} />
                    </div>
                    <div className="billing-field">
                      <label>Tax Type</label>
                      <select value={billingProfile.taxType} onChange={e => setBillingProfile(p => ({ ...p, taxType: e.target.value }))} disabled={billingProfile.billingType !== 'business'}>
                        <option value="">None</option>
                        <option value="VAT (EU)">VAT (EU)</option>
                        <option value="VAT (UK)">VAT (UK)</option>
                        <option value="GST (AU)">GST (AU)</option>
                        <option value="GST (IN)">GST (IN)</option>
                        <option value="EIN (US)">EIN (US)</option>
                        <option value="ABN (AU)">ABN (AU)</option>
                        <option value="Other">Other</option>
                      </select>
                    </div>
                    <div className="billing-field">
                      <label>Tax ID</label>
                      <input type="text" value={billingProfile.taxId} onChange={e => {
                        const val = e.target.value;
                        let autoType = '';
                        const v = val.replace(/\s/g, '').toUpperCase();
                        if (/^GB\d{1,12}$/.test(v)) autoType = 'VAT (UK)';
                        else if (/^(AT|BE|BG|CY|CZ|DE|DK|EE|EL|ES|FI|FR|HR|HU|IE|IT|LT|LU|LV|MT|NL|PL|PT|RO|SE|SI|SK)[A-Z0-9]*$/.test(v) && v.length >= 3) autoType = 'VAT (EU)';
                        else if (/^\d{2}-?\d{0,7}$/.test(v) && v.includes('-')) autoType = 'EIN (US)';
                        else if (/^\d{2}[A-Z]/.test(v) && v.length >= 5) autoType = 'GST (IN)';
                        else if (/^\d{6,11}$/.test(v) && billingProfile.businessCountry === 'AU') autoType = 'ABN (AU)';
                        else if (/^\d{6,11}$/.test(v)) autoType = 'GST (AU)';
                        setBillingProfile(p => ({ ...p, taxId: val, ...(autoType ? { taxType: autoType } : {}) }));
                      }} disabled={billingProfile.billingType !== 'business'} placeholder={
                        billingProfile.taxType === 'VAT (EU)' ? 'e.g. DE123456789' :
                        billingProfile.taxType === 'VAT (UK)' ? 'e.g. GB123456789' :
                        billingProfile.taxType === 'EIN (US)' ? 'e.g. 12-3456789' :
                        billingProfile.taxType === 'ABN (AU)' ? 'e.g. 51824753556' :
                        billingProfile.taxType === 'GST (IN)' ? 'e.g. 22AAAAA0000A1Z5' :
                        billingProfile.taxType === 'GST (AU)' ? 'e.g. 51824753556' :
                        'Enter your tax ID'
                      } />
                      {billingProfile.taxType === 'VAT (EU)' && (
                        <span className="billing-field-hint">Must include 2-letter country prefix (e.g. DE, FR, NL)</span>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* ── Shared Address Section ── */}
              <div className="billing-section billing-section--active" style={{ marginTop: 0 }}>
                <div className="billing-section-header" style={{ cursor: 'default' }}>
                  <div className="billing-section-label">
                    <div className="billing-section-radio billing-section-radio--selected">
                      <i className="fa-solid fa-location-dot" style={{ fontSize: '10px' }}></i>
                    </div>
                    <div>
                      <h4><i className="fa-solid fa-map-location-dot"></i> Address</h4>
                      <p>Billing address for invoices</p>
                    </div>
                  </div>
                </div>
                <div className="billing-section-body">
                  <div className="billing-form-grid">
                    <div className="billing-field billing-field--full">
                      <label>Address</label>
                      <input type="text" value={billingProfile.businessAddress} onChange={e => setBillingProfile(p => ({ ...p, businessAddress: e.target.value }))} placeholder="123 Main Street" />
                    </div>
                    <div className="billing-field">
                      <label>City</label>
                      <input type="text" value={billingProfile.businessCity} onChange={e => setBillingProfile(p => ({ ...p, businessCity: e.target.value }))} />
                    </div>
                    <div className="billing-field">
                      <label>State / Region</label>
                      <input type="text" value={billingProfile.businessState} onChange={e => setBillingProfile(p => ({ ...p, businessState: e.target.value }))} />
                    </div>
                    <div className="billing-field">
                      <label>ZIP / Postal Code</label>
                      <input type="text" value={billingProfile.businessZip} onChange={e => setBillingProfile(p => ({ ...p, businessZip: e.target.value }))} />
                    </div>
                    <div className="billing-field">
                      <label>Country</label>
                      <CountrySelect value={billingProfile.businessCountry} onChange={val => setBillingProfile(p => ({ ...p, businessCountry: val }))} />
                    </div>
                  </div>
                </div>
              </div>

              <button className="billing-save-btn" onClick={saveBillingProfile} disabled={billingSaving}>
                {billingSaving ? <><i className="fa-solid fa-spinner fa-spin"></i> Saving...</> : <><i className="fa-solid fa-floppy-disk"></i> Save Details</>}
              </button>
            </div>
          </div>
        )}
        {activeTab === 'invoices' && (
          <div className="invoices-tab">
            {invoicesLoading ? (
              <div className="loading-container"><div className="loading-spinner"></div></div>
            ) : invoices.length === 0 ? (
              <div className="placeholder-tab">
                <div className="placeholder-tab-icon"><i className="fa-solid fa-file-invoice"></i></div>
                <h3>No Invoices Yet</h3>
                <p>Your invoices will appear here after your first payment.</p>
              </div>
            ) : (
              <div className="invoices-table-wrap">
                <table className="invoices-table">
                  <thead>
                    <tr>
                      <th>Invoice #</th>
                      <th>Date</th>
                      <th>Amount</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map(inv => (
                      <tr key={inv.id}>
                        <td className="inv-number">{inv.number || '—'}</td>
                        <td>{new Date(inv.date * 1000).toLocaleDateString()}</td>
                        <td>{inv.currency} {(inv.amount / 100).toFixed(2)}</td>
                        <td><span className={`inv-status inv-status--${inv.status}`}>{inv.status}</span></td>
                        <td className="inv-actions">
                          {inv.pdfUrl && <a href={inv.pdfUrl} target="_blank" rel="noopener noreferrer" className="inv-link"><i className="fa-solid fa-download"></i> PDF</a>}
                          {inv.hostedUrl && <a href={inv.hostedUrl} target="_blank" rel="noopener noreferrer" className="inv-link"><i className="fa-solid fa-arrow-up-right-from-square"></i> View</a>}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {activeTab === 'support' && (
          <div className="support-tab">
            {/* Global agent availability status banner */}
            <div style={{
              display:'flex',alignItems:'center',gap:'10px',
              padding:'10px 16px',borderRadius:'10px',marginBottom:'14px',
              background: supportStatus==='online' ? 'rgba(16,185,129,0.07)' : 'rgba(245,158,11,0.07)',
              border: `1px solid ${supportStatus==='online'?'rgba(16,185,129,0.25)':supportStatus==='away'?'rgba(245,158,11,0.25)':supportStatus==='busy'?'rgba(249,115,22,0.25)':'rgba(107,114,128,0.25)'}`,
            }}>
              <span style={{
                width:'9px',height:'9px',borderRadius:'50%',flexShrink:0,
                background: supportStatus==='online'?'#10b981':supportStatus==='away'?'#f59e0b':supportStatus==='busy'?'#f97316':'#6b7280',
                boxShadow: supportStatus==='online'?'0 0 0 3px rgba(16,185,129,0.2)':'none',
                display:'inline-block'
              }}/>
              <div>
                <span style={{fontSize:'13px',fontWeight:'600',color:supportStatus==='online'?'#059669':supportStatus==='away'?'#d97706':supportStatus==='busy'?'#ea580c':'#4b5563'}}>
                  Support is {supportStatus==='online'?'Online':supportStatus==='away'?'Away':supportStatus==='busy'?'Busy':'Offline'}
                </span>
                <span style={{fontSize:'12px',color:'var(--gray-500)',marginLeft:'8px'}}>
                  {supportStatus==='online' && 'We\'re available and responding to tickets'}
                  {supportStatus==='away' && '— we\'ll respond within a few hours'}
                  {supportStatus==='busy' && '— response times may be longer than usual'}
                  {supportStatus==='offline' && '— we\'ll respond on the next business day'}
                </span>
              </div>
            </div>
            {ticketsLoading ? (
              <div className="loading-container"><div className="loading-spinner"></div></div>
            ) : selectedTicket ? (
              /* ── Ticket Detail View (Chat UI) ── */
              <div className="ticket-chat-wrapper">
                {/* Chat header */}
                <div className="ticket-chat-header">
                  <button className="ticket-chat-back" onClick={() => { setSelectedTicket(null); setTicketReplies([]); setReplyAttachments([]); clearInterval(pollRef.current); }}>
                    <i className="fa-solid fa-arrow-left"></i>
                  </button>
                  <div className="ticket-chat-header-info">
                    <div className="ticket-chat-title">#{selectedTicket.id}: {selectedTicket.subject}</div>
                    <div className="ticket-chat-meta">
                      <span className={`ticket-status ticket-status--${selectedTicket.status}`}>{selectedTicket.status.replace('_', ' ')}</span>
                      {selectedTicket.domain_name && <span className="ticket-chat-domain"><i className="fa-solid fa-globe"></i> {selectedTicket.domain_name}</span>}
                      {selectedTicket.plan_slug && <span className="ticket-chat-plan"><i className="fa-solid fa-cube"></i> {selectedTicket.plan_slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}</span>}
                    </div>
                  </div>
                  <div className="ticket-chat-header-actions">
                    {getSupportTier(selectedTicket.plan_slug) && (() => { const tier = getSupportTier(selectedTicket.plan_slug); return (
                      <span className={`ticket-sla-pill ${tier.cls}`}><i className={`fa-solid ${tier.icon}`}></i> {tier.label}</span>
                    );})()}
                    <label className="ticket-email-switch" title={selectedTicket.user_email_notifications !== 0 ? 'Email updates ON — click to disable' : 'Email updates OFF — click to enable'}>
                      <input type="checkbox"
                        checked={selectedTicket.user_email_notifications !== 0}
                        onChange={() => {
                          const newVal = selectedTicket.user_email_notifications === 0 ? 1 : 0;
                          setSelectedTicket(t => ({ ...t, user_email_notifications: newVal }));
                          axios.patch(`${API_URL}/api/support/tickets/${selectedTicket.id}/email-notifications`, { enabled: newVal === 1 }, getAuthConfig()).catch(() => {});
                        }}
                      />
                      <span className="ticket-email-switch-track"></span>
                      <span className="ticket-email-switch-label"><i className="fa-solid fa-envelope"></i> Email updates</span>
                    </label>
                  </div>
                </div>

                {/* Chat messages */}
                <div className="ticket-chat-messages" ref={chatMessagesRef}>
                  {ticketReplies.length === 0 && (
                    <div className="ticket-chat-empty">No messages yet — send your first message below.</div>
                  )}
                  {ticketReplies.map((r, idx) => {
                    const isUser = r.sender_type === 'customer';
                    const showDate = idx === 0 || new Date(r.created_at).toDateString() !== new Date(ticketReplies[idx - 1].created_at).toDateString();
                    const time = new Date(r.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
                    return (
                      <div key={r.id}>
                        {showDate && (
                          <div className="ticket-chat-date-divider">
                            <span>{new Date(r.created_at).toLocaleDateString([], { weekday: 'long', month: 'short', day: 'numeric' })}</span>
                          </div>
                        )}
                        <div className={`ticket-chat-row ticket-chat-row--${isUser ? 'user' : 'admin'}`}>
                          {!isUser && <div className="ticket-chat-avatar"><i className="fa-solid fa-headset"></i></div>}
                          <div className={`ticket-chat-bubble ticket-chat-bubble--${isUser ? 'user' : 'admin'}`}>
                            {r.message && <div className="ticket-chat-bubble-text">{r.message}</div>}
                            {r.attachments && r.attachments.length > 0 && (
                              <div className="ticket-chat-bubble-images">
                                {r.attachments.map((src, i) => (
                                  <img key={i} src={`${API_URL}${src}`} alt="attachment" onClick={() => setLightboxImage(`${API_URL}${src}`)} />
                                ))}
                              </div>
                            )}
                            <div className="ticket-chat-bubble-time">{time}</div>
                            {isUser && (
                              <div className="ticket-chat-ticks">
                                {r.read_at
                                  ? <><i className="fa-solid fa-check"></i><i className="fa-solid fa-check" style={{color:'#40E0D0',marginLeft:'-5px'}}></i></>
                                  : <i className="fa-solid fa-check" style={{color:'rgba(0,0,0,0.4)'}}></i>
                                }
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                  {typingIndicator && (
                    <div className="ticket-chat-row ticket-chat-row--admin">
                      <div className="ticket-chat-avatar"><i className="fa-solid fa-headset"></i></div>
                      <div className="ticket-chat-typing">
                        <span></span><span></span><span></span>
                      </div>
                    </div>
                  )}
                  <div ref={chatBottomRef} />
                </div>

                {/* Chat input bar */}
                {selectedTicket.status !== 'closed' ? (
                  <div className="ticket-chat-input-bar">
                    {replyAttachments.length > 0 && (
                      <div className="ticket-chat-attach-previews">
                        {replyAttachments.map((f, i) => (
                          <div key={i} className="ticket-chat-attach-thumb">
                            <img src={URL.createObjectURL(f)} alt="preview" />
                            <button onClick={() => removeAttachment(i, setReplyAttachments)}>&times;</button>
                          </div>
                        ))}
                      </div>
                    )}
                    <div className="ticket-chat-input-row">
                      <label className="ticket-chat-attach-btn" title="Attach image">
                        <i className="fa-solid fa-paperclip"></i>
                        <input type="file" accept="image/png,image/jpeg,image/gif,image/webp" multiple hidden onChange={e => handleFileSelect(e, setReplyAttachments)} />
                      </label>
                      <textarea
                        ref={replyTextareaRef}
                        className="ticket-chat-textarea"
                        placeholder="Type a message…"
                        value={replyMessage}
                        onChange={e => {
                          setReplyMessage(e.target.value);
                          if (selectedTicket) {
                            clearTimeout(typingDebounceRef.current);
                            typingDebounceRef.current = setTimeout(() => {
                              axios.post(`${API_URL}/api/support/tickets/${selectedTicket.id}/typing`, {}, getAuthConfig()).catch(() => {});
                            }, 400);
                          }
                        }}
                        onKeyDown={handleReplyKeyDown}
                        rows={1}
                      />
                      <button className="ticket-chat-send-btn" onClick={handleTicketReply} disabled={(!replyMessage.trim() && replyAttachments.length === 0) || replySending}>
                        {replySending ? <i className="fa-solid fa-spinner fa-spin"></i> : <i className="fa-solid fa-paper-plane"></i>}
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="ticket-chat-closed-bar">This ticket is closed. Open a new ticket if you need further help.</div>
                )}
              </div>
            ) : showNewTicketForm ? (
              /* ── New Ticket Form ── */
              <div className="ticket-new-form">
                <button className="ticket-back-btn" onClick={() => { setShowNewTicketForm(false); setTicketAttachments([]); }}>
                  <i className="fa-solid fa-arrow-left"></i> Back to tickets
                </button>
                <h3>Create New Ticket</h3>
                <form onSubmit={handleCreateTicket}>
                  <div className="ticket-form-group">
                    <label>Subject *</label>
                    <input type="text" value={newTicket.subject} onChange={e => setNewTicket(t => ({ ...t, subject: e.target.value }))} placeholder="Brief description of your issue" required />
                  </div>
                  <div className="ticket-form-group">
                    <label>Category</label>
                    <select value={newTicket.category} onChange={e => setNewTicket(t => ({ ...t, category: e.target.value }))}>
                      <option value="general">General</option>
                      <option value="technical">Technical</option>
                      <option value="billing">Billing</option>
                      <option value="feature_request">Feature Request</option>
                    </select>
                  </div>
                  {hasAnySubscription && (
                  <div className="ticket-form-group">
                    <label>Linked Subscription *</label>
                    <select value={newTicket.subscription_id} onChange={e => setNewTicket(t => ({ ...t, subscription_id: e.target.value }))} required>
                      <option value="">Select a subscription...</option>
                      {displaySubs.length > 0 && (
                        <optgroup label="Active subscriptions">
                          {displaySubs.map(s => (
                            <option key={s.id} value={s.id}>{s.planName || s.id} — {s.domain || getServiceName(s.service)}</option>
                          ))}
                        </optgroup>
                      )}
                      {cancelledHistory.length > 0 && (
                        <optgroup label="Cancelled subscriptions">
                          {cancelledHistory.map(s => (
                            <option key={s.id} value={s.id}>
                              {s.planName || s.id} — {s.domain || getServiceName(s.service)}{s.canceledAt ? ` (cancelled ${new Date(s.canceledAt).toLocaleDateString()})` : ''}
                            </option>
                          ))}
                        </optgroup>
                      )}
                    </select>
                  </div>
                  )}
                  <div className="ticket-form-group">
                    <label>Message *</label>
                    <textarea value={newTicket.message} onChange={e => setNewTicket(t => ({ ...t, message: e.target.value }))} placeholder="Describe your issue in detail..." rows={6} required />
                  </div>
                  <div className="ticket-form-group">
                    <label>Screenshots (optional)</label>
                    <div
                      className={`ticket-dropzone ${dragActive ? 'ticket-dropzone--active' : ''}`}
                      onDragOver={handleDragOver}
                      onDragLeave={handleDragLeave}
                      onDrop={e => handleDrop(e, setTicketAttachments)}
                      onClick={() => document.getElementById('ticket-file-input').click()}
                    >
                      <i className="fa-solid fa-cloud-arrow-up"></i>
                      <span>Drag & drop images here or click to browse</span>
                      <small>PNG, JPG, GIF, WebP — max 5MB each, up to 5 files</small>
                      <input id="ticket-file-input" type="file" accept="image/png,image/jpeg,image/gif,image/webp" multiple hidden onChange={e => handleFileSelect(e, setTicketAttachments)} />
                    </div>
                    {ticketAttachments.length > 0 && (
                      <div className="ticket-attachment-previews">
                        {ticketAttachments.map((f, i) => (
                          <div key={i} className="ticket-attachment-preview">
                            <img src={URL.createObjectURL(f)} alt="preview" />
                            <button type="button" className="ticket-attachment-remove" onClick={() => removeAttachment(i, setTicketAttachments)}>&times;</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="ticket-form-actions">
                    <button type="button" className="ticket-btn-secondary" onClick={() => { setShowNewTicketForm(false); setTicketAttachments([]); }}>Cancel</button>
                    <button type="submit" className="ticket-btn-primary" disabled={ticketSubmitting}>{ticketSubmitting ? 'Submitting...' : 'Submit Ticket'}</button>
                  </div>
                </form>
              </div>
            ) : (
              /* ── Ticket List (Card-based) ── */
              <div className="ticket-list">
                <div className="ticket-list-header">
                  <h3>Support Tickets</h3>
                  <button className="ticket-btn-primary" onClick={() => setShowNewTicketForm(true)}>
                    <i className="fa-solid fa-plus"></i> New Ticket
                  </button>
                </div>
                <div className="ticket-filters">
                  {['all', 'open', 'in_progress', 'resolved', 'closed'].map(f => (
                    <button key={f} className={`ticket-filter-chip ${ticketStatusFilter === f ? 'active' : ''}`} onClick={() => setTicketStatusFilter(f)}>
                      {f === 'all' ? 'All' : f.replace('_', ' ')}
                    </button>
                  ))}
                </div>
                {tickets.filter(t => ticketStatusFilter === 'all' || t.status === ticketStatusFilter).length === 0 ? (
                  <div className="empty-state">
                    <i className="fa-solid fa-ticket" style={{ fontSize: '32px', color: '#555', marginBottom: '12px' }}></i>
                    <h3>No Tickets</h3>
                    <p>{ticketStatusFilter === 'all' ? "You haven't created any support tickets yet." : `No ${ticketStatusFilter.replace('_', ' ')} tickets.`}</p>
                  </div>
                ) : (
                  <div className="ticket-card-list">
                    {tickets.filter(t => ticketStatusFilter === 'all' || t.status === ticketStatusFilter).map(t => (
                      <div key={t.id} className="ticket-card" onClick={() => loadTicketDetail(t.id)}>
                        <div className="ticket-card-top">
                          <span className="ticket-card-id">#{t.id}</span>
                          <span className={`ticket-status ticket-status--${t.status}`}>{t.status.replace('_', ' ')}</span>
                        </div>
                        <div className="ticket-card-subject">{t.subject}</div>
                        {(t.domain_name || t.plan_slug) && (
                          <div className="ticket-card-plan-row">
                            {t.plan_slug && <span className="ticket-plan-badge"><i className="fa-solid fa-cube"></i> {t.plan_slug.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}</span>}
                            {t.domain_name && <span className="ticket-domain-badge"><i className="fa-solid fa-globe"></i> {t.domain_name}</span>}
                          </div>
                        )}
                        <div className="ticket-card-bottom">
                          <span className="ticket-category-badge">{t.category}</span>
                          <span className="ticket-card-date">{new Date(t.updated_at).toLocaleDateString()}</span>
                        </div>
                        {getSupportTier(t.plan_slug) && (
                          <div className={`ticket-sla-row ${getSupportTier(t.plan_slug).cls}`}>
                            <i className={`fa-solid ${getSupportTier(t.plan_slug).icon}`}></i>
                            <span className="ticket-sla-label">{getSupportTier(t.plan_slug).label}</span>
                            <span className="ticket-sla-desc">{getSupportTier(t.plan_slug).desc}</span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Image Lightbox */}
        {lightboxImage && (
          <div className="ticket-lightbox" onClick={() => setLightboxImage(null)}>
            <button className="ticket-lightbox-close" onClick={() => setLightboxImage(null)}>&times;</button>
            <img src={lightboxImage} alt="Full size" onClick={e => e.stopPropagation()} />
          </div>
        )}
      </div>
    </div>
  );
}
